import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  DollarSign,
  ChevronLeft,
  Save,
  Plus,
  Edit2,
  Copy,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  Settings,
  CreditCard,
  Banknote,
  Receipt,
  Percent,
  Trophy,
  Building2,
  FileText,
  Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Mock purse structures
const mockPurseStructures = [
  {
    id: 1,
    name: 'Standard Feature Purse',
    race_type: 'A Feature',
    total_purse: 50000,
    is_default: true,
    positions: {
      1: 10000, 2: 5500, 3: 3500, 4: 2800, 5: 2500,
      6: 2300, 7: 2100, 8: 2000, 9: 1900, 10: 1800,
      11: 1700, 12: 1600, 13: 1500, 14: 1400, 15: 1300,
      16: 1200, 17: 1100, 18: 1000, 19: 950, 20: 900,
      21: 850, 22: 800, 23: 750, 24: 700
    }
  },
  {
    id: 2,
    name: 'Preliminary Night Purse',
    race_type: 'Preliminary',
    total_purse: 25000,
    is_default: true,
    positions: {
      1: 5000, 2: 2500, 3: 1500, 4: 1200, 5: 1000,
      6: 900, 7: 850, 8: 800, 9: 750, 10: 700,
      11: 650, 12: 600, 13: 575, 14: 550, 15: 525,
      16: 500, 17: 500, 18: 500, 19: 500, 20: 500,
      21: 500, 22: 500, 23: 500, 24: 500
    }
  },
  {
    id: 3,
    name: 'Heat Race Tow Money',
    race_type: 'Heat',
    total_purse: 0,
    is_default: true,
    positions: {
      1: 50, 2: 0, 3: 0, 4: 0, 5: 0,
      6: 0, 7: 0, 8: 0
    }
  }
];

// Mock entry fee structures
const mockEntryFees = [
  { id: 1, name: 'Standard Entry', fee: 50, description: 'Regular event entry fee' },
  { id: 2, name: 'Major Event Entry', fee: 100, description: 'Crown Jewel and special events' },
  { id: 3, name: 'Mini Series Entry', fee: 75, description: 'Multi-night mini series' }
];

// Mock payment methods
const mockPaymentMethods = [
  { id: 1, name: 'Cash', enabled: true, icon: Banknote },
  { id: 2, name: 'Check', enabled: true, icon: FileText },
  { id: 3, name: 'Credit Card', enabled: true, icon: CreditCard },
  { id: 4, name: 'Wire Transfer', enabled: false, icon: Building2 }
];

// Mock deduction types
const mockDeductions = [
  { id: 1, name: 'Series Points Fund', percentage: 5, fixed: 0, enabled: true },
  { id: 2, name: 'Year-End Fund', percentage: 2, fixed: 0, enabled: true },
  { id: 3, name: 'Tire Lease', percentage: 0, fixed: 250, enabled: true },
  { id: 4, name: 'Insurance', percentage: 0, fixed: 100, enabled: false }
];

// Purse Structure Card
function PurseStructureCard({ structure, isExpanded, onToggle, onEdit, onDelete, onDuplicate }) {
  const positionCount = Object.keys(structure.positions).length;
  const maxPayout = Math.max(...Object.values(structure.positions));

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800">{structure.name}</h3>
              {structure.is_default && (
                <Badge variant="primary" size="sm">Default</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">{structure.race_type}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-lg font-bold text-emerald-600">
              ${structure.total_purse.toLocaleString()}
            </p>
            <p className="text-xs text-slate-500">{positionCount} positions</p>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-200">
          {/* Actions */}
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              Winner: ${maxPayout.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => onEdit(structure)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDuplicate(structure)}>
                <Copy className="w-4 h-4 mr-1" />
                Duplicate
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(structure)} className="text-red-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Positions Grid */}
          <div className="p-4">
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {Object.entries(structure.positions).map(([pos, amount]) => (
                <div
                  key={pos}
                  className={`p-2 rounded-lg text-center ${
                    parseInt(pos) === 1 ? 'bg-amber-100 border border-amber-200' :
                    parseInt(pos) <= 3 ? 'bg-emerald-50 border border-emerald-200' :
                    'bg-slate-50 border border-slate-200'
                  }`}
                >
                  <div className="text-xs text-slate-500">P{pos}</div>
                  <div className={`font-bold ${
                    parseInt(pos) === 1 ? 'text-amber-700' :
                    parseInt(pos) <= 3 ? 'text-emerald-700' :
                    'text-slate-700'
                  }`}>
                    ${amount.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Entry Fee Card
function EntryFeeCard({ fee, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Receipt className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h4 className="font-medium text-slate-800">{fee.name}</h4>
          <p className="text-sm text-slate-500">{fee.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xl font-bold text-slate-800">${fee.fee}</span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(fee)}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(fee)} className="text-red-500">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Deduction Card
function DeductionCard({ deduction, onToggle, onEdit }) {
  return (
    <div className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
      deduction.enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'
    }`}>
      <div className="flex items-center gap-3">
        <button
          onClick={() => onToggle(deduction.id)}
          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
            deduction.enabled
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-slate-300 bg-white'
          }`}
        >
          {deduction.enabled && <Check className="w-4 h-4" />}
        </button>
        <div>
          <h4 className="font-medium text-slate-800">{deduction.name}</h4>
          <p className="text-sm text-slate-500">
            {deduction.percentage > 0 && `${deduction.percentage}% of winnings`}
            {deduction.percentage > 0 && deduction.fixed > 0 && ' + '}
            {deduction.fixed > 0 && `$${deduction.fixed} flat fee`}
          </p>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onEdit(deduction)}>
        <Edit2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Payment Method Toggle
function PaymentMethodToggle({ method, onToggle }) {
  const Icon = method.icon;
  return (
    <button
      onClick={() => onToggle(method.id)}
      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
        method.enabled
          ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
          : 'bg-slate-50 border-slate-200 text-slate-400'
      }`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-sm font-medium">{method.name}</span>
      {method.enabled ? (
        <Badge variant="success" size="sm">Enabled</Badge>
      ) : (
        <Badge variant="neutral" size="sm">Disabled</Badge>
      )}
    </button>
  );
}

// Purse Editor Modal
function PurseEditorModal({ isOpen, onClose, onSave, structure }) {
  const [formData, setFormData] = useState({
    name: structure?.name || '',
    race_type: structure?.race_type || 'A Feature',
    total_purse: structure?.total_purse || 0,
    positions: structure?.positions || {}
  });
  const [positionCount, setPositionCount] = useState(
    structure ? Object.keys(structure.positions).length : 24
  );

  if (!isOpen) return null;

  const handleApplyFormula = (formula) => {
    const totalPurse = formData.total_purse || 50000;
    let positions = {};

    switch (formula) {
      case 'standard':
        // Standard payout curve
        for (let i = 1; i <= positionCount; i++) {
          if (i === 1) positions[i] = Math.round(totalPurse * 0.2);
          else if (i <= 3) positions[i] = Math.round(totalPurse * (0.12 - (i - 2) * 0.02));
          else if (i <= 10) positions[i] = Math.round(totalPurse * (0.07 - (i - 4) * 0.005));
          else positions[i] = Math.round(totalPurse * 0.02);
        }
        break;
      case 'top-heavy':
        // Winner takes more
        for (let i = 1; i <= positionCount; i++) {
          if (i === 1) positions[i] = Math.round(totalPurse * 0.3);
          else if (i === 2) positions[i] = Math.round(totalPurse * 0.12);
          else if (i === 3) positions[i] = Math.round(totalPurse * 0.08);
          else positions[i] = Math.round((totalPurse * 0.5) / (positionCount - 3));
        }
        break;
      case 'flat':
        // Equal payout to all
        const perPosition = Math.round(totalPurse / positionCount);
        for (let i = 1; i <= positionCount; i++) {
          positions[i] = perPosition;
        }
        break;
      default:
        break;
    }

    setFormData({ ...formData, positions });
  };

  const handlePositionChange = (pos, value) => {
    setFormData({
      ...formData,
      positions: {
        ...formData.positions,
        [pos]: parseInt(value) || 0
      }
    });
  };

  const calculateTotal = () => {
    return Object.values(formData.positions).reduce((sum, val) => sum + val, 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">
            {structure ? 'Edit Purse Structure' : 'New Purse Structure'}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Structure Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Standard Feature Purse"
            />
            <Select
              label="Race Type"
              value={formData.race_type}
              onChange={(e) => setFormData({ ...formData, race_type: e.target.value })}
            >
              <option value="A Feature">A Feature</option>
              <option value="B Feature">B Feature (LCS)</option>
              <option value="Preliminary">Preliminary</option>
              <option value="Heat">Heat Race</option>
              <option value="Dash">Dash</option>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              type="number"
              label="Total Purse"
              value={formData.total_purse}
              onChange={(e) => setFormData({ ...formData, total_purse: parseInt(e.target.value) || 0 })}
              placeholder="50000"
            />
            <Input
              type="number"
              label="Number of Positions"
              value={positionCount}
              onChange={(e) => setPositionCount(parseInt(e.target.value) || 1)}
              min="1"
              max="50"
            />
          </div>

          {/* Quick Apply Formulas */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quick Apply</label>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => handleApplyFormula('standard')}>
                Standard Curve
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleApplyFormula('top-heavy')}>
                Top Heavy
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleApplyFormula('flat')}>
                Flat Payout
              </Button>
            </div>
          </div>

          {/* Position Payouts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Position Payouts</label>
              <span className={`text-sm font-medium ${
                calculateTotal() === formData.total_purse ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                Total: ${calculateTotal().toLocaleString()} / ${formData.total_purse.toLocaleString()}
              </span>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-60 overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {Array.from({ length: positionCount }, (_, i) => i + 1).map((pos) => (
                <div key={pos} className="text-center">
                  <label className="text-xs text-slate-500">P{pos}</label>
                  <Input
                    type="number"
                    value={formData.positions[pos] || 0}
                    onChange={(e) => handlePositionChange(pos, e.target.value)}
                    className="text-center text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>
            <Save className="w-4 h-4 mr-1" />
            Save Structure
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentsConfig() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  // State
  const [purseStructures, setPurseStructures] = useState(mockPurseStructures);
  // eslint-disable-next-line no-unused-vars
  const [entryFees, setEntryFees] = useState(mockEntryFees);
  const [deductions, setDeductions] = useState(mockDeductions);
  const [paymentMethods, setPaymentMethods] = useState(mockPaymentMethods);
  const [expandedPurse, setExpandedPurse] = useState(null);
  const [showPurseModal, setShowPurseModal] = useState(false);
  const [editingPurse, setEditingPurse] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  const series = currentSeries || { name: 'Unknown Series', color: '#3b5998' };

  // Handlers
  const handleTogglePurse = (id) => {
    setExpandedPurse(expandedPurse === id ? null : id);
  };

  const handleEditPurse = (structure) => {
    setEditingPurse(structure);
    setShowPurseModal(true);
  };

  const handleDuplicatePurse = (structure) => {
    const newStructure = {
      ...structure,
      id: Date.now(),
      name: `${structure.name} (Copy)`,
      is_default: false
    };
    setPurseStructures([...purseStructures, newStructure]);
    setHasChanges(true);
  };

  const handleDeletePurse = (structure) => {
    if (window.confirm(`Delete "${structure.name}"?`)) {
      setPurseStructures(purseStructures.filter(p => p.id !== structure.id));
      setHasChanges(true);
    }
  };

  const handleSavePurse = (formData) => {
    if (editingPurse) {
      setPurseStructures(purseStructures.map(p =>
        p.id === editingPurse.id ? { ...p, ...formData } : p
      ));
    } else {
      setPurseStructures([...purseStructures, { ...formData, id: Date.now(), is_default: false }]);
    }
    setShowPurseModal(false);
    setEditingPurse(null);
    setHasChanges(true);
  };

  const handleToggleDeduction = (id) => {
    setDeductions(deductions.map(d =>
      d.id === id ? { ...d, enabled: !d.enabled } : d
    ));
    setHasChanges(true);
  };

  const handleTogglePaymentMethod = (id) => {
    setPaymentMethods(paymentMethods.map(m =>
      m.id === id ? { ...m, enabled: !m.enabled } : m
    ));
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    // Simulate API save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    setHasChanges(false);
  };

  // Calculate summary stats
  const totalPurseValue = purseStructures.reduce((sum, p) => sum + p.total_purse, 0);
  const activeDeductions = deductions.filter(d => d.enabled).length;

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/${seriesSlug}/season`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-6 h-6 text-emerald-500" />
              <h1 className="text-2xl font-bold text-slate-800">Payments Configuration</h1>
            </div>
            <p className="text-slate-500">Configure purse structures, entry fees, and payment settings for {series.name}</p>
          </div>

          <Button onClick={handleSaveAll} disabled={!hasChanges || saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{purseStructures.length}</p>
                <p className="text-xs text-slate-500">Purse Structures</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{entryFees.length}</p>
                <p className="text-xs text-slate-500">Entry Fee Types</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Percent className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{activeDeductions}</p>
                <p className="text-xs text-slate-500">Active Deductions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">${(totalPurseValue / 1000).toFixed(0)}K</p>
                <p className="text-xs text-slate-500">Total Purse Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Purse Structures */}
        <div className="lg:col-span-2 space-y-6">
          {/* Purse Structures */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Purse Structures</CardTitle>
                  <CardDescription>Configure payouts for different race types</CardDescription>
                </div>
                <Button onClick={() => { setEditingPurse(null); setShowPurseModal(true); }}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Structure
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {purseStructures.map((structure) => (
                  <PurseStructureCard
                    key={structure.id}
                    structure={structure}
                    isExpanded={expandedPurse === structure.id}
                    onToggle={() => handleTogglePurse(structure.id)}
                    onEdit={handleEditPurse}
                    onDelete={handleDeletePurse}
                    onDuplicate={handleDuplicatePurse}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Entry Fees */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Entry Fees</CardTitle>
                  <CardDescription>Standard entry fees for different event types</CardDescription>
                </div>
                <Button variant="secondary" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Fee
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {entryFees.map((fee) => (
                  <EntryFeeCard
                    key={fee.id}
                    fee={fee}
                    onEdit={() => {}}
                    onDelete={() => {}}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Methods */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>Accepted payment types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {paymentMethods.map((method) => (
                  <PaymentMethodToggle
                    key={method.id}
                    method={method}
                    onToggle={handleTogglePaymentMethod}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Deductions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Deductions</CardTitle>
                  <CardDescription>Automatic withholdings from winnings</CardDescription>
                </div>
                <Button variant="ghost" size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {deductions.map((deduction) => (
                  <DeductionCard
                    key={deduction.id}
                    deduction={deduction}
                    onToggle={handleToggleDeduction}
                    onEdit={() => {}}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle>Related Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Link
                  to={`/${seriesSlug}/config/points`}
                  className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <span className="text-sm text-slate-700">Points Configuration</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </Link>
                <Link
                  to={`/${seriesSlug}/config/season`}
                  className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Settings className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-700">Season Settings</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Purse Editor Modal */}
      <PurseEditorModal
        isOpen={showPurseModal}
        onClose={() => { setShowPurseModal(false); setEditingPurse(null); }}
        onSave={handleSavePurse}
        structure={editingPurse}
      />
    </div>
  );
}
